import { Tooltip } from '@lib/components'
import { selectSectionsByTab } from '@octopus-do/octopus-project-core'
import { Button } from '@octopus-do/octopus-storybook'
import { useProjectLegacyActions } from '@store/migrationActions/useProjectLegacyActions'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { selectNothingVisibleAmong } from '@store/reducers/tags/visibility'
import { getId } from '@store/utils'
import { removeFilterByTag } from '@storeOld/reducers/canvas/canvas'
import cx from 'classnames'
import type React from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { useArtboardMode } from '@/hooks'
import type { AppState } from '@/types'
import { Section } from './Section'
import css from './Sections.module.scss'

const Sections: React.FC = () => {
  const activeTab = useSelector(migrationProjectSelector.getActiveOrFirstTabId)
  const sectionIds = useSelector(selectSectionsByTab(activeTab), shallowEqual)

  const { addSection } = useProjectLegacyActions()

  const dispatch = useDispatch()

  // @todo remove this after implementing order/index refactoring in octopus-project-core
  const sortedSectionIds = useSelector(
    migrationProjectSelector.getProjectSectionIdsSortedByIds(sectionIds),
    shallowEqual
  )
  const canEdit: boolean = useSelector(
    migrationProjectSelector.getProjectAccessCanEdit
  )

  const { seoMode, gallery } = useArtboardMode()

  // Page-tag filter (OCT-5007): a tab keeps its place in the navigation even
  // when nothing on it matches, so say so instead of showing a blank artboard.
  const nothingMatchesOnTab = useSelector(
    selectNothingVisibleAmong(sortedSectionIds)
  )

  const selectedTags = useSelector(
    ({ canvas }: AppState) => canvas.tags.selectedTags,
    shallowEqual
  )

  const onClearTagFilter = () => {
    selectedTags.forEach((tagId) => {
      dispatch(removeFilterByTag(tagId))
    })
  }

  const onAddGroup = () => {
    sendGtmEvent(GtmTriggerName.SECTION_ADD, 'Add section')
    const id = getId()

    addSection({ id })
  }

  if (nothingMatchesOnTab) {
    return (
      <div className={css.noMatches}>
        <p className={css.noMatchesText}>No pages match the selected tags</p>
        <Button
          priority="secondary"
          size="small"
          label="Clear filter"
          onClick={onClearTagFilter}
        />
      </div>
    )
  }

  return (
    <>
      {sortedSectionIds.map((sectionId: string, index: number) => (
        <Section
          key={`${sectionId}-${index}`}
          id={sectionId}
          index={index}
          last={index === sortedSectionIds.length - 1}
        />
      ))}

      {canEdit && !seoMode && !gallery && (
        <div className={css.addWrap}>
          <Tooltip
            content="Add section"
            position="top"
            tooltipClassName="tooltip"
          >
            <div className={cx(css.add, 'Tree-btn')}>
              <div onClick={onAddGroup}>
                <i className="fa fa-plus" />
              </div>
            </div>
          </Tooltip>
        </div>
      )}
    </>
  )
}

export default Sections
